import type { Person } from '../../domain/types.js';
import { householdPortions } from '../../domain/portions.js';
import { stapleIdsNeeded } from '../../domain/pantry.js';
import {
  aggregateRequirements, dinnerRequirements, lunchRequirements, stapleRequirements,
} from '../../domain/requirements.js';
import type { MealCatalogue } from '../recipes/catalogue.js';
import { withoutUnresolved } from '../recipes/resolve.js';
import { toWeekPlan, type StoredPlan } from '../store/db.js';

/**
 * The cupboard staples this plan needs, de-duplicated. Shared so the list and
 * the cupboard screen cannot drift apart about what "needed" means.
 *
 * Deliberately ignores the pantry answers themselves — this is "what would the
 * plan ask for", not "what's left to buy". CupboardCheck uses that to render
 * every question; ShoppingList uses it to know how many of those questions were
 * answered "yes", so a stale answer from a week ago doesn't silently vanish a
 * staple with no way to see it happened.
 */
export function staplesNeeded(plan: StoredPlan, people: Person[], catalogue: MealCatalogue): string[] {
  const week = toWeekPlan(withoutUnresolved(plan, catalogue.byId).plan);
  const household = householdPortions(people);
  const { lookups } = catalogue;
  const aggregated = aggregateRequirements([
    ...dinnerRequirements(week.dinners, household, lookups),
    ...lunchRequirements(week.lunches, lookups, people),
    ...stapleRequirements(week.staples, lookups),
  ]);
  return stapleIdsNeeded(aggregated, lookups.ingredients);
}
